import { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";

/**
 * Modal: pulls hours from Chrona for a given date range + role and feeds
 * them into Margix's internals/externals state, just like a manual Excel
 * upload would.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   onLoaded    (kind: "internals" | "externals", data: object) => void
 *               Called on success with the JSON shape that Margix's
 *               existing setInternals/setExternals consumers expect.
 */
const today = () => new Date().toISOString().slice(0, 10);
// Default the "From" date to the start of the data history (1 Jan 2023) so a
// pull covers everything by default — Koen's request. The user can still narrow
// the range before pulling.
const DEFAULT_FROM = "2023-01-01";

export default function PullFromChronaModal({ open, onClose, onLoaded }) {
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(today());
  const [role, setRole] = useState("internals"); // 'internals' | 'externals'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset error every time the modal is reopened so the user doesn't see
  // a stale message from a previous attempt.
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const chronaToken = localStorage.getItem("chrona_token");
    if (!chronaToken) {
      setError(
        "No Chrona token saved. Log out and log in again to refresh it."
      );
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/excel/import/from-chrona`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Margix JWT for the new /excel/* auth; X-Chrona-Token is the
            // separate Chrona credential used to pull the export.
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            "X-Chrona-Token": chronaToken,
          },
          body: JSON.stringify({
            date_from: from,
            date_to: to,
            role: role,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Pull failed");
        return;
      }

      onLoaded(role, data);
      onClose();
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      // Not while a pull is running. Closing mid-flight did not cancel the
      // fetch: it resolved afterwards and still wrote the hours slot, so
      // [pull → click away → Reset] resurrected the data and [pull → click
      // away → upload a file] silently overwrote the upload. The Cancel
      // button was already guarded; the backdrop was the way around it.
      onClick={() => !loading && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 24,
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <h3 style={{ margin: 0, marginBottom: 6, fontSize: 18 }}>
          Pull hours from Chrona
        </h3>
        <p
          style={{
            margin: 0,
            marginBottom: 18,
            color: "#64748b",
            fontSize: 13,
          }}
        >
          Pulls submitted and unsubmitted hours for the date range and role
          you select. Loads them into Margix the same way a manual Excel
          upload would.
        </p>

        <label
          style={{ display: "block", fontSize: 12, color: "#475569", marginBottom: 4 }}
        >
          Role
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={inputStyle}
        >
          <option value="internals">Internals</option>
          <option value="externals">Externals</option>
        </select>

        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#475569",
                marginBottom: 4,
              }}
            >
              From
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#475569",
                marginBottom: 4,
              }}
            >
              To
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 13,
              padding: "8px 10px",
              borderRadius: 6,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{ ...btnStyle, background: "#fff", color: "#475569" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || from > to}
            style={{
              ...btnStyle,
              background: loading ? "#94a3b8" : "#2563eb",
              color: "#fff",
            }}
          >
            {loading ? "Pulling…" : "Pull"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  outline: "none",
  background: "#fff",
};

const btnStyle = {
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  cursor: "pointer",
};
